import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
  Field as FormikField,
  type FieldProps,
  type FormikErrors,
  type FormikTouched,
  type FormikValues,
} from 'formik';
import * as React from 'react';
import { uid } from 'react-uid';
import { Connector } from '../../../../app/ApiGen';
import type { FormField } from '../../../../design-system';
import type { FormFields } from '../../../../utils/formUtils';
import type { DetailsOther } from '../../../../views/Organisations/Organisation/Create/CreateChargePoint/types';
import { Detail } from '../../../Detail';
import { TextField } from '../../../TextField';

export type ConnectorsForm = Pick<DetailsOther, 'connectors'>;

export type ConnectorFormFields = Pick<
  Connector,
  'connectorFormat' | 'connectorType' | 'maxAmperage' | 'maxVoltage' | 'powerType'
>;

type ConnectorSectionProps = {
  name: string;
  sectionId: number;
  errors: FormikErrors<FormikValues>;
  touched: FormikTouched<FormikValues>;
  fields: FormFields<FormikValues>;
  arrayHelpers: { remove: (id: number) => void };
};

export const ConnectorSection: React.FC<ConnectorSectionProps> = ({
  arrayHelpers,
  errors,
  fields,
  name,
  sectionId,
  touched,
}) => {
  function getItem<T>(item: FormikErrors<FormikValues> | FormikTouched<FormikValues>): Record<string, T> {
    const itemByName = item && item[name];
    if (itemByName) {
      const itemsAsRecord = itemByName as Record<string, T>[];
      return itemsAsRecord[sectionId] ?? {};
    }
    return {};
  }

  const fieldErrors: Record<string, string> = getItem<string>(errors);
  const fieldsTouched: Record<string, boolean> = getItem<boolean>(touched);

  return (
    <Box>
      <Grid container justifyContent="space-between">
        <Typography>{`Connector ${sectionId + 1}`}</Typography>
        <IconButton size="small" onClick={() => arrayHelpers.remove(sectionId)}>
          <CloseIcon />
        </IconButton>
      </Grid>
      {Object.values(fields).map<React.ReactElement>(
        ({ id, label, inputComponent: InputComponent, inputProps }: FormField, idx) => {
          const error: string = fieldsTouched[id] && !!fieldErrors[id] ? fieldErrors[id] : '';
          const styling = { paddingTop: '8px' };
          return (
            <FormikField key={uid(id, idx)} name={`${name}.${sectionId}.${id}`}>
              {({ field }: FieldProps<string | number>) => (
                <Detail label={label}>
                  {InputComponent ? (
                    <InputComponent
                      checked={field.checked}
                      disabled={false}
                      error={!!error}
                      helperText={error}
                      multiple={field.multiple}
                      name={field.name}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      style={styling}
                      value={field.value}
                    />
                  ) : (
                    <TextField
                      InputProps={inputProps?.InputProps}
                      error={!!error}
                      fullWidth
                      helperText={error}
                      inputElementProps={{ 'aria-label': `${id}-input` }}
                      name={field.name}
                      onBlur={field.onBlur}
                      onChange={field.onChange}
                      type={inputProps?.type}
                      value={field.value}
                    />
                  )}
                </Detail>
              )}
            </FormikField>
          );
        },
      )}
    </Box>
  );
};
